import React, { useState, useEffect, useCallback } from 'react';
import {
  UserIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlayIcon,
  StopIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  SignalIcon,
  ClockIcon,
  ComputerDesktopIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { api, getSocketOrigin } from '../services/api';
import toast from 'react-hot-toast';
import { AgentDesktopSetup } from '../components/admin/AgentDesktopSetup';

// ─── Snapchat ghost SVG icon ──────────────────────────────────────────────────
const SnapchatIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.024.015.06.029.12.029.307-.007.706-.222 1.074-.422.3-.168.6-.33.867-.33.195 0 .398.046.577.138.698.352.696 1.08.695 1.18 0 .403-.126.726-.406.973-.198.18-.434.33-.669.479-.36.222-.733.452-.941.762-.065.097-.082.19-.056.292l.815 2.516c.352 1.093-.213 1.93-1.352 2.157-.186.039-.378.059-.567.059a4.063 4.063 0 0 1-1.556-.306c-.466-.196-.914-.452-1.346-.7-.506-.294-1.003-.583-1.48-.736-.317-.104-.655-.148-.988-.148-.33 0-.661.044-.98.148-.48.154-.982.443-1.49.738-.432.248-.88.504-1.348.7A4.071 4.071 0 0 1 7.26 17.8c-.189 0-.381-.02-.567-.059-1.14-.227-1.704-1.064-1.352-2.157l.815-2.516c.026-.102.009-.195-.056-.292-.208-.31-.581-.54-.94-.762-.236-.15-.472-.3-.67-.479-.28-.247-.406-.57-.406-.973-.001-.1-.003-.828.694-1.18.179-.092.382-.138.577-.138.267 0 .567.162.867.33.367.2.764.415 1.074.422.061 0 .098-.014.12-.03a9.68 9.68 0 0 1-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.857 1.07 11.214.793 12.206.793z"/>
  </svg>
);

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:          { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',     label: 'Active' },
    paused:          { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', label: 'Paused' },
    waiting_login:   { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',         label: 'Waiting Login' },
    indexing:        { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', label: 'Indexing' },
    session_expired: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', label: 'Session Expired' },
    banned:          { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',             label: 'Banned' },
    error:           { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',             label: 'Error' },
  };
  const { color, label } = map[status] || map.error;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
};

// ─── Phase badge ──────────────────────────────────────────────────────────────
const PhaseBadge = ({ phase }) => {
  const phaseColors = {
    initial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    phase_1: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    phase_2: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    phase_3: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
    cta:     'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    completed:'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${phaseColors[phase] || phaseColors.initial}`}>
      {phase?.replace('_', ' ')}
    </span>
  );
};

const UserTypeBadge = ({ userType }) => {
  const map = {
    horny: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    talker: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    lonely: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    buyer: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  const t = userType || 'unknown';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[t] || map.unknown}`}>
      {t}
    </span>
  );
};

const UserProfilePanel = ({ profile, userType, isDarkMode, compact = false }) => {
  const p = profile || {};
  const items = [
    { label: 'Location', value: p.location },
    { label: 'Work', value: p.work },
    { label: 'Hobbies', value: p.hobbies?.length ? p.hobbies.join(', ') : null },
    { label: 'Social', value: p.socialLife },
    { label: 'Age', value: p.age },
    { label: 'Status', value: p.relationshipStatus },
  ].filter(x => x.value);
  const hasData = items.length > 0 || p.notes || p.highVisualInterest;

  return (
    <div className={`rounded-lg border ${compact ? 'p-2.5' : 'p-3'} ${
      isDarkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Lead profile
        </p>
        <UserTypeBadge userType={userType} />
      </div>
      {!hasData ? (
        <p className="text-xs text-gray-400 italic">No details yet — builds as he chats</p>
      ) : (
        <dl className={`space-y-1 ${compact ? 'text-xs' : 'text-sm'}`}>
          {items.map(({ label, value }) => (
            <div key={label} className="flex gap-2">
              <dt className={`shrink-0 w-16 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{label}</dt>
              <dd className={`flex-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{value}</dd>
            </div>
          ))}
          {p.highVisualInterest && (
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-300/90' : 'text-yellow-700'}`}>
              📸 High visual interest (selfie sender)
            </p>
          )}
          {p.notes && (
            <p className={`text-xs mt-1 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{p.notes}</p>
          )}
        </dl>
      )}
    </div>
  );
};

/** Compact phase progress bar — mirrors X Chatbot ConversationProgressLine */
const SnapPhaseProgress = ({ conversation }) => {
  const numberOfPhases = conversation.modelId?.conversationPhases?.numberOfPhases || 2;
  const enablePostCTAPhases = conversation.modelId?.ctaPhase?.enablePostCTAPhases || false;
  const postCTAPhases = conversation.modelId?.ctaPhase?.postCTAPhases || [];

  const phases = ['initial'];
  for (let i = 1; i <= numberOfPhases; i++) phases.push(`phase_${i}`);
  phases.push('cta');
  if (enablePostCTAPhases && postCTAPhases.length > 0) {
    [...postCTAPhases]
      .sort((a, b) => a.phaseNumber - b.phaseNumber)
      .forEach(p => phases.push(`phase_${p.phaseNumber}`));
  }

  const isCompleted = conversation.conversationPhase === 'completed';
  const currentPhaseIndex = isCompleted
    ? phases.length - 1
    : phases.indexOf(conversation.conversationPhase);
  const progress = isCompleted
    ? 100
    : currentPhaseIndex >= 0
      ? ((currentPhaseIndex + 1) / phases.length) * 100
      : (1 / phases.length) * 100;

  const getGradient = (phase) => {
    const map = {
      initial: 'from-blue-500 to-blue-600',
      phase_1: 'from-blue-500 via-yellow-500 to-yellow-600',
      phase_2: 'from-blue-500 via-yellow-500 via-orange-500 to-orange-600',
      phase_3: 'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 to-pink-600',
      cta:     'from-blue-500 via-yellow-500 via-orange-500 via-red-500 to-red-600',
      completed: 'from-green-500 to-green-600',
    };
    return map[phase] || 'from-gray-500 to-gray-600';
  };

  const getLabel = (phase) => {
    if (phase === 'initial') return 'Init';
    if (phase === 'cta') return 'CTA';
    const m = phase.match(/phase_(\d+)/);
    return m ? `P${m[1]}` : phase;
  };

  return (
    <div className="w-full h-5 rounded-full overflow-hidden relative bg-gray-200/80 dark:bg-gray-700/80">
      <div
        className={`h-full bg-gradient-to-r ${getGradient(conversation.conversationPhase)} transition-all duration-500`}
        style={{ width: `${Math.max(8, progress)}%` }}
      />
      <div className="absolute inset-0 flex items-center justify-between px-2">
        {phases.map((phase, i) => (
          <span
            key={phase}
            className={`text-[9px] font-semibold leading-none ${
              i <= currentPhaseIndex ? 'text-white drop-shadow-sm' : 'text-transparent'
            }`}
          >
            {getLabel(phase)}
          </span>
        ))}
      </div>
    </div>
  );
};

const AMS_TZ = 'Europe/Amsterdam';

const formatAmsterdamTs = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: AMS_TZ,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(d);
  const get = (type) => parts.find(p => p.type === type)?.value || '';
  const day = get('day');
  const month = get('month');
  const hour = get('hour');
  const minute = get('minute');
  const second = get('second');
  if (!day || !month) return '—';
  return `${day} ${month} ${hour}:${minute}:${second}`;
};

/** Full AMS datetime for tooltips (includes CET/CEST). */
const formatAmsterdamDateTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: AMS_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  }).format(d);
};

const formatLatestTs = formatAmsterdamTs;

/** Prefer @username; else short conversation / Snap user id (no bulk hydrate). */
function snapPeerLabel(conv) {
  if (conv?.snapTargetUsername) return `@${conv.snapTargetUsername}`;
  if (conv?.snapTargetDisplayName) return conv.snapTargetDisplayName;
  if (conv?.snapTargetUserId) return `id:${String(conv.snapTargetUserId).slice(0, 8)}…`;
  if (conv?.snapConversationUuid) return `chat:${String(conv.snapConversationUuid).slice(0, 8)}…`;
  return 'Unknown';
}

/** CapitalAI-style scan defaults — synced with backend SnapAccount.config */
const CAPITALAI_SCAN_DEFAULTS = {
  replyDelayMinMs: 3000,
  replyDelayMaxMs: 6000,
  activePollIntervalMinMs: 6000,
  activePollIntervalMaxMs: 10000,
  idlePollIntervalMs: 30000,
  backgroundInboxScanIntervalMs: 15000,
  indexMissedOnly: true,
  indexHydrateUsernames: true,

  maxConcurrentDeepScans: 3,
  maxConcurrentConvos: 3,
  markConversationsRead: true,
  headed: true
};

// ─── Add Account Modal ────────────────────────────────────────────────────────
const AddAccountModal = ({ onClose, onSave, agents, models }) => {
  const { isDarkMode } = useTheme();
  const [form, setForm] = useState({
    displayName: '', username: '', gologinProfileId: '',
    vaAgentId: '', modelId: '', proxyString: '', config: { ...CAPITALAI_SCAN_DEFAULTS }
  });
  const [saving, setSaving] = useState(false);

  const selectedAgent = agents.find(a => a._id === form.vaAgentId) || null;
  const agentFolder = selectedAgent?.gologinFolder?.name || null;
  const willAutoCreate = form.vaAgentId && agentFolder && !form.gologinProfileId.trim();

  const handleSave = async () => {
    if (!form.displayName || !form.username || !form.vaAgentId) {
      toast.error('Display name, Snapchat username and VA agent are required');
      return;
    }
    if (!form.gologinProfileId.trim() && !agentFolder) {
      toast.error('Either supply a GoLogin Profile ID or configure a GoLogin folder on the selected agent first.');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
  } focus:outline-none focus:ring-1 focus:ring-blue-500`;

  const labelCls = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`relative w-full max-w-lg mx-4 rounded-xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Add Snapchat Account
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Display Name</label>
            <input className={inputCls} placeholder="My Snap Account" value={form.displayName}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Snapchat Username</label>
            <input className={inputCls} placeholder="username (without @)" value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace('@','') }))} />
          </div>

          {/* VA Agent selector */}
          <div>
            <label className={labelCls}>VA Agent (VPS)</label>
            <select className={inputCls} value={form.vaAgentId}
              onChange={e => setForm(f => ({ ...f, vaAgentId: e.target.value }))}>
              <option value="">Select VA agent…</option>
              {agents.map(a => (
                <option key={a._id} value={a._id}>
                  {a.name || a._id}{a.gologinFolder?.name ? ` · 📁 ${a.gologinFolder.name}` : ' · ⚠ no folder'}
                </option>
              ))}
            </select>
            {selectedAgent && !agentFolder && (
              <p className="mt-1 text-xs text-orange-500">
                No GoLogin folder on this agent — supply a Profile ID manually or set a folder in Virtual Assistants → Agent Setup.
              </p>
            )}
            {selectedAgent && agentFolder && (
              <p className="mt-1 text-xs text-green-500">
                GoLogin folder: <strong>{agentFolder}</strong> — profile auto-created if you leave Profile ID blank.
              </p>
            )}
          </div>

          {/* Proxy string — same format as X accounts */}
          <div>
            <label className={labelCls}>
              Proxy
              <span className="ml-1 text-xs font-normal text-gray-400">(recommended — baked into GoLogin profile)</span>
            </label>
            <input
              className={inputCls}
              placeholder="HOST:PORT:USERNAME:PASSWORD[:PROXY_AUTH]"
              value={form.proxyString}
              onChange={e => setForm(f => ({ ...f, proxyString: e.target.value.trim() }))}
            />
            <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              e.g. v2.proxyempire.io:5000:user:pass:pool-tag
            </p>
          </div>

          {/* GoLogin Profile ID — optional when agent has folder */}
          <div>
            <label className={labelCls}>
              GoLogin Profile ID
              <span className="ml-1 text-xs font-normal text-gray-400">(optional — auto-created from agent folder)</span>
            </label>
            <input className={inputCls}
              placeholder={willAutoCreate ? `Will auto-create in folder "${agentFolder}"` : 'GoLogin profile UUID'}
              value={form.gologinProfileId}
              onChange={e => setForm(f => ({ ...f, gologinProfileId: e.target.value }))} />
            {willAutoCreate && (
              <p className="mt-1 text-xs text-blue-400">
                Leave blank — GoLogin profile <code>snap-{form.username || 'username'}</code> will be created in <strong>{agentFolder}</strong>.
              </p>
            )}
          </div>

          <div>
            <label className={labelCls}>AI Model</label>
            <select className={inputCls} value={form.modelId}
              onChange={e => setForm(f => ({ ...f, modelId: e.target.value }))}>
              <option value="">Select model…</option>
              {models.map(m => (
                <option key={m._id} value={m._id}>{m.displayName || m.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Reply Delay Min (ms)</label>
              <input type="number" className={inputCls} value={form.config.replyDelayMinMs}
                onChange={e => setForm(f => ({ ...f, config: { ...f.config, replyDelayMinMs: +e.target.value } }))} />
            </div>
            <div>
              <label className={labelCls}>Reply Delay Max (ms)</label>
              <input type="number" className={inputCls} value={form.config.replyDelayMaxMs}
                onChange={e => setForm(f => ({ ...f, config: { ...f.config, replyDelayMaxMs: +e.target.value } }))} />
            </div>
          </div>
          <div className={`rounded-lg border p-3 text-xs leading-relaxed ${isDarkMode ? 'border-gray-600 bg-gray-900/40 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
            <p className={`font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Scan mode (CapitalAI-style)</p>
            Memory inbox refresh every poll · deep-scan unread/active only · {CAPITALAI_SCAN_DEFAULTS.maxConcurrentDeepScans} concurrent threads · mark as read after scan.
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-50">
            {saving
              ? (willAutoCreate ? 'Creating GoLogin profile…' : 'Saving…')
              : 'Add Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Message Drawer ───────────────────────────────────────────────────────────
const resolveSnapMessagePhotoSrc = (msg) => {
  if (msg?.photoDisplayUrl) return msg.photoDisplayUrl;
  const url = msg?.photoUrl;
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/uploads/')) return `${getSocketOrigin()}${url}`;
  return url;
};

const SnapBotPhotoPreview = ({ msg, isDarkMode }) => {
  const src = resolveSnapMessagePhotoSrc(msg);
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    if (!msg?.photoUrl && msg?.mediaType !== 'image') return null;
    return (
      <div className={`flex items-center gap-1.5 mb-1.5 px-1 py-1 rounded-lg ${isDarkMode ? 'bg-black/20' : 'bg-black/5'}`}>
        <PhotoIcon className="h-4 w-4 opacity-60 shrink-0" />
        <span className="text-xs opacity-60">Photo snap</span>
      </div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="block mb-1.5">
      <img
        src={src}
        alt="Bot photo snap"
        className="rounded-lg h-28 w-28 object-cover border border-black/10"
        onError={() => setFailed(true)}
      />
    </a>
  );
};

const MessageDrawer = ({ conversation, onClose }) => {
  const { isDarkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileMeta, setProfileMeta] = useState(null);

  useEffect(() => {
    if (!conversation) return;
    setLoading(true);
    setProfileMeta(null);
    api.get(`/snap/conversations/${conversation._id}/messages`)
      .then(r => {
        setMessages(r.data?.messages || []);
        if (r.data?.userProfile || r.data?.userType) {
          setProfileMeta({
            userProfile: r.data.userProfile,
            userType: r.data.userType,
            conversationPhase: r.data.conversationPhase
          });
        }
      })
      .catch(() => setMessages(conversation.messages || []))
      .finally(() => setLoading(false));
  }, [conversation]);

  if (!conversation) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative w-full max-w-md h-full flex flex-col shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {conversation.snapTargetDisplayName || snapPeerLabel(conversation)}
            </p>
            <p className="text-xs text-gray-400">
              {conversation.snapTargetUsername
                ? `@${conversation.snapTargetUsername}`
                : (conversation.snapTargetUserId
                  ? `user ${conversation.snapTargetUserId}`
                  : 'no @handle')}
              {' · '}
              {conversation.snapConversationUuid?.slice(0, 8)}…
            </p>
          </div>
          <div className="flex items-center gap-2">
            <PhaseBadge phase={conversation.conversationPhase} />
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <UserProfilePanel
            profile={profileMeta?.userProfile ?? conversation.userProfile}
            userType={profileMeta?.userType ?? conversation.userType}
            isDarkMode={isDarkMode}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">No messages yet</p>
          ) : (
            messages.map((msg, i) => {
              const isBot = msg.role === 'bot' || msg.role === 'assistant';
              const isMediaDesc = /^\*guy sends /i.test(String(msg.content || '').trim());
              const hasPhoto = !!(msg.photoDisplayUrl || msg.photoUrl);
              const mediaLabel = !hasPhoto && msg.mediaType && msg.mediaType !== 'text'
                ? String(msg.mediaType).toUpperCase()
                : (isMediaDesc ? 'SNAP' : null);
              return (
              <div key={i} className={`flex ${isBot ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs px-3 py-2 rounded-2xl text-sm ${
                  isBot
                    ? 'bg-yellow-400 text-black'
                    : isDarkMode
                      ? 'bg-gray-700 text-gray-100'
                      : 'bg-gray-100 text-gray-900'
                }`}>
                  {isBot && (hasPhoto || msg.mediaType === 'image') && (
                    <SnapBotPhotoPreview msg={msg} isDarkMode={isDarkMode} />
                  )}
                  {msg.content ? (
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  ) : null}
                  <p className={`text-xs mt-1 opacity-60`}>
                    {isBot ? 'BOT' : 'User'}
                    {mediaLabel ? ` · ${mediaLabel}` : ''}
                    {hasPhoto && !mediaLabel ? ' · PHOTO' : ''}
                    {' · '}
                    {msg.timestamp ? formatAmsterdamTs(msg.timestamp) : ''}
                    {isBot && msg.delivered !== false && ' ✓'}
                  </p>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Snap Agent Setup Tab ─────────────────────────────────────────────────────
/**
 * Renders the desktop-agent download + pairing token + GoLogin token UI for
 * every VA agent that has automationType === 'snap_chat'.
 *
 * Uses the existing AgentDesktopSetup component — same component as the
 * follow-automation setup in Virtual Assistants. The VPS agents (Ryzen 1/2,
 * EPYC 1) can reuse the same GoLogin API key already configured there.
 */
const SnapAgentSetupTab = ({ agents, isDarkMode, onRefresh }) => {
  const snapAgents = agents.filter(a => a.automationType === 'snap_chat');
  const allAgents  = agents; // show all so admins can see non-snap agents too

  const [agentDetails, setAgentDetails] = useState({});
  const [loadingId, setLoadingId]       = useState(null);
  const [expandedId, setExpandedId]     = useState(null);

  const loadAgent = useCallback(async (agentId) => {
    if (agentDetails[agentId]) return; // already loaded
    setLoadingId(agentId);
    try {
      const res = await api.get(`/admin/va/ai-agents/${agentId}`);
      const agent = res.data?.agent || res.data;
      if (agent?._id) {
        setAgentDetails(prev => ({ ...prev, [agentId]: agent }));
      }
    } catch (_) {}
    finally { setLoadingId(null); }
  }, [agentDetails]);

  const toggleExpand = useCallback(async (agentId) => {
    if (expandedId === agentId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(agentId);
    await loadAgent(agentId);
  }, [expandedId, loadAgent]);

  const cardCls = `rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <div className="space-y-6">
      {/* How-to header */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-start gap-3">
          <ComputerDesktopIcon className={`h-5 w-5 mt-0.5 shrink-0 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
          <div className="space-y-3 flex-1">
            <div>
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Desktop agent setup — Snapchat Chatbot
              </p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                The same desktop-agent that runs follow automation on Ryzen 1/2 and EPYC 1 also handles
                Snapchat. No new software needed on the VPS — just set the agent's automation type to
                <code className="mx-1 px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono">snap_chat</code>
                and download its pre-configured zip below.
              </p>
            </div>
            <div className="space-y-2">
              {[
                { n: 1, text: 'Pick a VA agent (or create a new one in Virtual Assistants → AI VA)' },
                { n: 2, text: 'Set its automation type to snap_chat in the VA detail page' },
                { n: 3, text: 'Expand the agent below → download zip → unzip on VPS → npm install && npm start' },
                { n: 4, text: 'GoLogin API token is already set on your existing agents — no need to re-enter it' },
                { n: 5, text: 'Add Snapchat accounts above and assign them to this agent' },
              ].map(step => (
                <div key={step.n} className="flex items-start gap-2.5">
                  <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 ${
                    isDarkMode ? 'bg-yellow-400/20 text-yellow-300' : 'bg-yellow-50 text-yellow-600'
                  }`}>{step.n}</span>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Snap agents section */}
      {snapAgents.length > 0 && (
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
            Snapchat agents
          </p>
          <div className="space-y-2">
            {snapAgents.map(agent => (
              <AgentSetupRow
                key={agent._id}
                agent={agentDetails[agent._id] || agent}
                expanded={expandedId === agent._id}
                loading={loadingId === agent._id}
                onToggle={() => toggleExpand(agent._id)}
                onAgentUpdated={updated => setAgentDetails(prev => ({ ...prev, [agent._id]: { ...(prev[agent._id] || agent), ...updated } }))}
                isDarkMode={isDarkMode}
                highlight
              />
            ))}
          </div>
        </div>
      )}

      {/* All agents (so any agent can be set up for Snap) */}
      {allAgents.filter(a => a.automationType !== 'snap_chat').length > 0 && (
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Other agents (can be repurposed for Snapchat)
          </p>
          <div className="space-y-2">
            {allAgents.filter(a => a.automationType !== 'snap_chat').map(agent => (
              <AgentSetupRow
                key={agent._id}
                agent={agentDetails[agent._id] || agent}
                expanded={expandedId === agent._id}
                loading={loadingId === agent._id}
                onToggle={() => toggleExpand(agent._id)}
                onAgentUpdated={updated => setAgentDetails(prev => ({ ...prev, [agent._id]: { ...(prev[agent._id] || agent), ...updated } }))}
                isDarkMode={isDarkMode}
                highlight={false}
              />
            ))}
          </div>
        </div>
      )}

      {allAgents.length === 0 && (
        <div className={`${cardCls} flex flex-col items-center justify-center py-12 gap-2`}>
          <ComputerDesktopIcon className="h-8 w-8 text-gray-300" />
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No VA agents found</p>
          <p className="text-xs text-gray-400">Create a VA agent in Virtual Assistants → AI VA, then return here to set it up.</p>
        </div>
      )}
    </div>
  );
};

const AgentSetupRow = ({ agent, expanded, loading, onToggle, onAgentUpdated, isDarkMode, highlight }) => {
  const isOnline = agent.runtime?.lastSeenAt &&
    (Date.now() - new Date(agent.runtime.lastSeenAt).getTime() < 60_000);

  const cardCls = `rounded-xl border overflow-hidden ${
    highlight
      ? isDarkMode ? 'border-yellow-500/30 bg-gray-800' : 'border-yellow-200 bg-white'
      : isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
  }`;

  return (
    <div className={cardCls}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
          isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <span className={`h-2 w-2 rounded-full shrink-0 ${isOnline ? 'bg-green-400' : 'bg-gray-400'}`} />
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {agent.name || agent._id}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {agent.automationType || 'follow'}
              </span>
              {agent.runtime?.host && (
                <span className="text-xs text-gray-400">· {agent.runtime.host}</span>
              )}
              {isOnline && (
                <span className="text-xs text-green-500">· online</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {highlight && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
              isDarkMode ? 'bg-yellow-400/20 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
            }`}>
              Snapchat
            </span>
          )}
          {loading ? (
            <ArrowPathIcon className="h-4 w-4 text-gray-400 animate-spin" />
          ) : (
            <ChevronRightIcon className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          )}
        </div>
      </button>

      {expanded && (
        <div className={`px-5 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <AgentDesktopSetup
            agent={agent}
            compact
            onAgentUpdated={onAgentUpdated}
          />
        </div>
      )}
    </div>
  );
};

// ─── Start account: All vs Per reply mode ─────────────────────────────────────
const StartReplyModeModal = ({ account, onClose, onConfirm, isDarkMode }) => {
  const [mode, setMode] = useState('all');
  const [wipeConversations, setWipeConversations] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(mode, { wipeConversations });
      onClose();
    } catch (_) {
      // toast handled by caller
    } finally {
      setBusy(false);
    }
  };

  const optionCls = (active) =>
    `w-full text-left rounded-xl border p-4 transition-colors ${
      active
        ? isDarkMode
          ? 'border-yellow-400 bg-yellow-400/10'
          : 'border-yellow-400 bg-yellow-50'
        : isDarkMode
          ? 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`w-full max-w-md rounded-2xl shadow-xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Start @{account.username}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">How should AI replies work?</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <button type="button" className={optionCls(mode === 'all')} onClick={() => setMode('all')}>
            <div className="flex items-center justify-between">
              <span className={`font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                All conversations
              </span>
              <span className="text-[10px] uppercase tracking-wide font-semibold text-yellow-600 dark:text-yellow-400">
                Recommended
              </span>
            </div>
            <p className={`text-xs mt-1.5 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Reply everywhere they spoke last. Agent reads inbox from browser memory, deep-scans only unread threads (3 at a time), then queues AI replies.
            </p>
          </button>

          <button type="button" className={optionCls(mode === 'per')} onClick={() => setMode('per')}>
            <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Per conversation
            </span>
            <p className={`text-xs mt-1.5 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Legacy — you Start/Stop each thread manually. Will be removed once All is stable.
            </p>
          </button>

          <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${
            isDarkMode ? 'border-gray-600 bg-gray-800/30' : 'border-gray-200 bg-gray-50'
          }`}>
            <input
              type="checkbox"
              className="mt-0.5"
              checked={wipeConversations}
              onChange={e => setWipeConversations(e.target.checked)}
            />
            <span>
              <span className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Clear conversation history first
              </span>
              <span className={`block text-xs mt-1 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Deletes all Mongo threads for @{account.username} so the next scan starts fresh. Recommended after changing scan strategy.
              </span>
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={busy}
            className={`px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-50">
            {busy ? 'Starting…' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_INCOMING_ADDS = {
  enabled: false,
  requireDisplayHit: true,
  customApproveNames: [],
  blockUsernames: [],
  acceptBurstMin: 2,
  acceptBurstMax: 5,
  acceptGapMinSec: 1.1,
  acceptGapMaxSec: 2.2,
  declineBurstMin: 2,
  declineBurstMax: 5,
  declineGapMinSec: 3.6,
  declineGapMaxSec: 4.8,
  betweenBurstsMinSec: 2,
  betweenBurstsMaxSec: 5,
  acceptDailyLimit: 50,
  declineDailyLimit: 50,
  scanIntervalMinMin: 10,
  scanIntervalMaxMin: 20
};

function msToSec(ms, fallback) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return fallback;
  return Math.round((n / 1000) * 10) / 10;
}

function secToMs(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 1000);
}

function hydrateIncomingAddsForm(raw) {
  const c = { ...DEFAULT_INCOMING_ADDS, ...(raw || {}) };
  return {
    enabled: !!c.enabled,
    requireDisplayHit: c.requireDisplayHit !== false,
    customApproveNamesText: (c.customApproveNames || []).join(', '),
    blockUsernamesText: (c.blockUsernames || []).join(', '),
    acceptBurstMin: c.acceptBurstMin ?? 2,
    acceptBurstMax: c.acceptBurstMax ?? 5,
    acceptGapMinSec: c.acceptGapMinSec
      ?? msToSec(c.acceptGapMinMs ?? c.acceptDelayMinMs, 1.1),
    acceptGapMaxSec: c.acceptGapMaxSec
      ?? msToSec(c.acceptGapMaxMs ?? c.acceptDelayMaxMs, 2.2),
    declineBurstMin: c.declineBurstMin ?? 2,
    declineBurstMax: c.declineBurstMax ?? 5,
    declineGapMinSec: c.declineGapMinSec
      ?? msToSec(c.declineGapMinMs ?? c.declineDelayMinMs, 3.6),
    declineGapMaxSec: c.declineGapMaxSec
      ?? msToSec(c.declineGapMaxMs ?? c.declineDelayMaxMs, 4.8),
    betweenBurstsMinSec: c.betweenBurstsMinSec
      ?? msToSec(c.betweenBurstsMinMs, 2),
    betweenBurstsMaxSec: c.betweenBurstsMaxSec
      ?? msToSec(c.betweenBurstsMaxMs, 5),
    acceptDailyLimit: c.acceptDailyLimit ?? 50,
    declineDailyLimit: c.declineDailyLimit ?? 50,
    scanIntervalMinMin: c.scanIntervalMinMin
      ?? msToMin(c.scanIntervalMinMs ?? c.scanIntervalMs, 10),
    scanIntervalMaxMin: c.scanIntervalMaxMin
      ?? msToMin(c.scanIntervalMaxMs ?? c.scanIntervalMs, 20)
  };
}

function msToMin(ms, fallback) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return fallback;
  return Math.round((n / 60_000) * 10) / 10;
}

function minToMs(min) {
  const n = Number(min);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 60_000);
}

/** Compact incoming-adds settings + decision log. */
const IncomingAddsModal = ({ account, onClose, onSaved, isDarkMode }) => {
  const [form, setForm] = useState(() => hydrateIncomingAddsForm(account.config?.incomingAdds));
  const [actions, setActions] = useState([]);
  const [counts, setCounts] = useState({ approve: 0, decline: 0, todayApprove: 0, todayDecline: 0 });
  const [busy, setBusy] = useState(false);
  const [logFilter, setLogFilter] = useState('');
  const [loadingLog, setLoadingLog] = useState(true);

  const inputCls = `w-full px-2.5 py-1.5 rounded-lg border text-sm tabular-nums ${
    isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;
  const labelCls = `block text-[11px] font-medium mb-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const sectionCls = `rounded-xl border p-3 space-y-2.5 ${
    isDarkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50/70'
  }`;

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const loadLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      const params = new URLSearchParams({ limit: '60' });
      if (logFilter) params.set('action', logFilter);
      const res = await api.get(`/snap/accounts/${account._id}/incoming-adds?${params}`);
      setActions(res.data?.actions || []);
      setCounts(res.data?.counts || { approve: 0, decline: 0, todayApprove: 0, todayDecline: 0 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load log');
    } finally {
      setLoadingLog(false);
    }
  }, [account._id, logFilter]);

  useEffect(() => { loadLog(); }, [loadLog]);

  const clampPair = (lo, hi, cap = 30) => {
    let a = Math.max(0, Math.min(cap, Number(lo) || 0));
    let b = Math.max(0, Math.min(cap, Number(hi) || 0));
    if (b < a) b = a;
    return [a, b];
  };

  const save = async () => {
    setBusy(true);
    try {
      const customApproveNames = form.customApproveNamesText
        .split(/[,;\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
      const blockUsernames = form.blockUsernamesText
        .split(/[,;\n]+/).map(s => s.trim().toLowerCase().replace(/^@/, '')).filter(Boolean);

      const [acceptBurstMin, acceptBurstMax] = clampPair(form.acceptBurstMin, form.acceptBurstMax);
      const [declineBurstMin, declineBurstMax] = clampPair(form.declineBurstMin, form.declineBurstMax);
      let acceptGapMinMs = secToMs(form.acceptGapMinSec);
      let acceptGapMaxMs = secToMs(form.acceptGapMaxSec);
      if (acceptGapMaxMs < acceptGapMinMs) acceptGapMaxMs = acceptGapMinMs;
      let declineGapMinMs = secToMs(form.declineGapMinSec);
      let declineGapMaxMs = secToMs(form.declineGapMaxSec);
      if (declineGapMaxMs < declineGapMinMs) declineGapMaxMs = declineGapMinMs;
      let betweenBurstsMinMs = secToMs(form.betweenBurstsMinSec);
      let betweenBurstsMaxMs = secToMs(form.betweenBurstsMaxSec);
      if (betweenBurstsMaxMs < betweenBurstsMinMs) betweenBurstsMaxMs = betweenBurstsMinMs;

      const incomingAdds = {
        enabled: !!form.enabled,
        requireDisplayHit: !!form.requireDisplayHit,
        customApproveNames,
        blockUsernames,
        acceptBurstMin,
        acceptBurstMax,
        acceptGapMinMs,
        acceptGapMaxMs,
        declineBurstMin,
        declineBurstMax,
        declineGapMinMs,
        declineGapMaxMs,
        betweenBurstsMinMs,
        betweenBurstsMaxMs,
        acceptDailyLimit: Math.max(0, Number(form.acceptDailyLimit) || 0),
        declineDailyLimit: Math.max(0, Number(form.declineDailyLimit) || 0),
        scanIntervalMinMs: (() => {
          let lo = Math.max(30_000, minToMs(form.scanIntervalMinMin) || 10 * 60_000);
          let hi = Math.max(30_000, minToMs(form.scanIntervalMaxMin) || 20 * 60_000);
          if (hi < lo) hi = lo;
          return lo;
        })(),
        scanIntervalMaxMs: (() => {
          let lo = Math.max(30_000, minToMs(form.scanIntervalMinMin) || 10 * 60_000);
          let hi = Math.max(30_000, minToMs(form.scanIntervalMaxMin) || 20 * 60_000);
          if (hi < lo) hi = lo;
          return hi;
        })()
      };
      const res = await api.patch(`/snap/accounts/${account._id}`, { config: { incomingAdds } });
      toast.success(incomingAdds.enabled ? 'Saved — live on next scan' : 'Incoming adds off');
      onSaved?.(res.data?.account);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const MiniRange = ({ label, minKey, maxKey, step = 1 }) => (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] text-gray-400">Min</span>
          <input type="number" step={step} min={0} className={inputCls}
            value={form[minKey]} onChange={e => setField(minKey, e.target.value)} />
        </div>
        <div>
          <span className="text-[10px] text-gray-400">Max</span>
          <input type="number" step={step} min={0} className={inputCls}
            value={form[maxKey]} onChange={e => setField(maxKey, e.target.value)} />
        </div>
      </div>
    </div>
  );

  const todayA = counts.todayApprove ?? 0;
  const todayD = counts.todayDecline ?? 0;
  const limA = Number(form.acceptDailyLimit) || 0;
  const limD = Number(form.declineDailyLimit) || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col rounded-2xl shadow-xl border ${
        isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Incoming adds · @{account.username}
            </h3>
            <p className="text-[11px] text-gray-400">Live while chatbot runs</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-3 flex-1">
          <label className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 cursor-pointer ${
            form.enabled
              ? isDarkMode ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-emerald-300 bg-emerald-50'
              : isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Auto approve / decline
            </span>
            <input type="checkbox" className="h-4 w-4" checked={!!form.enabled}
              onChange={e => setField('enabled', e.target.checked)} />
          </label>

          <div className={sectionCls}>
            <p className={`text-xs font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>Accept</p>
            <MiniRange label="Burst size" minKey="acceptBurstMin" maxKey="acceptBurstMax" />
            <MiniRange label="Gap between accepts (s)" minKey="acceptGapMinSec" maxKey="acceptGapMaxSec" step={0.1} />
            <div>
              <label className={labelCls}>Daily limit (0 = ∞)</label>
              <input type="number" min={0} className={inputCls} value={form.acceptDailyLimit}
                onChange={e => setField('acceptDailyLimit', e.target.value)} />
              <p className="text-[10px] text-gray-400 mt-0.5">Today {todayA}{limA ? ` / ${limA}` : ''}</p>
            </div>
          </div>

          <div className={sectionCls}>
            <p className={`text-xs font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Decline</p>
            <MiniRange label="Burst size" minKey="declineBurstMin" maxKey="declineBurstMax" />
            <MiniRange label="Gap between declines (s)" minKey="declineGapMinSec" maxKey="declineGapMaxSec" step={0.1} />
            <div>
              <label className={labelCls}>Daily limit (0 = ∞)</label>
              <input type="number" min={0} className={inputCls} value={form.declineDailyLimit}
                onChange={e => setField('declineDailyLimit', e.target.value)} />
              <p className="text-[10px] text-gray-400 mt-0.5">Today {todayD}{limD ? ` / ${limD}` : ''}</p>
            </div>
          </div>

          <div className={`${sectionCls} space-y-2.5`}>
            <p className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Timing</p>
            <div>
              <label className={labelCls}>Pause A→D (s)</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step={0.1} min={0} className={inputCls} value={form.betweenBurstsMinSec}
                  onChange={e => setField('betweenBurstsMinSec', e.target.value)} placeholder="min" />
                <input type="number" step={0.1} min={0} className={inputCls} value={form.betweenBurstsMaxSec}
                  onChange={e => setField('betweenBurstsMaxSec', e.target.value)} placeholder="max" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Next scan every (min, random)</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step={0.5} min={0.5} className={inputCls} value={form.scanIntervalMinMin}
                  onChange={e => setField('scanIntervalMinMin', e.target.value)} placeholder="min" />
                <input type="number" step={0.5} min={0.5} className={inputCls} value={form.scanIntervalMaxMin}
                  onChange={e => setField('scanIntervalMaxMin', e.target.value)} placeholder="max" />
              </div>
            </div>
          </div>

          <div className={sectionCls}>
            <p className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filter</p>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={!!form.requireDisplayHit}
                onChange={e => setField('requireDisplayHit', e.target.checked)} />
              <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>Male name in display</span>
            </label>
            <div>
              <label className={labelCls}>Extra accept names</label>
              <input className={inputCls} placeholder="lars, sven"
                value={form.customApproveNamesText}
                onChange={e => setField('customApproveNamesText', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Always decline</label>
              <input className={inputCls} placeholder="spam1, bot"
                value={form.blockUsernamesText}
                onChange={e => setField('blockUsernamesText', e.target.value)} />
            </div>
          </div>

          <div className={`rounded-xl border p-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Log · {counts.approve}✓ {counts.decline}✗
              </p>
              <div className="flex items-center gap-1.5">
                <select className={`px-1.5 py-0.5 rounded border text-[11px] ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  value={logFilter} onChange={e => setLogFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="approve">Accept</option>
                  <option value="decline">Decline</option>
                </select>
                <button type="button" onClick={loadLog} className="p-1 rounded text-gray-400">
                  <ArrowPathIcon className={`h-3.5 w-3.5 ${loadingLog ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              {loadingLog ? (
                <p className="text-[11px] text-gray-400 py-3 text-center">Loading…</p>
              ) : actions.length === 0 ? (
                <p className="text-[11px] text-gray-400 py-3 text-center">Empty</p>
              ) : actions.map(a => (
                <div key={a._id} className="py-1.5 flex justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`text-xs truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      @{a.targetUsername || '?'}
                    </p>
                    <p className="text-[10px] text-gray-400 truncate">{a.reason || '—'}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold uppercase ${
                    a.action === 'approve' ? 'text-emerald-500' : 'text-red-500'
                  }`}>{a.action === 'approve' ? 'ok' : 'no'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button onClick={onClose} disabled={busy}
            className={`px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}>
            Cancel
          </button>
          <button onClick={save} disabled={busy}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Assign AI model to one or many Snap accounts. */
const AssignModelModal = ({ accountIds, accounts, models, onClose, onConfirm, isDarkMode }) => {
  const selected = (accounts || []).filter(a =>
    accountIds.some(id => String(id) === String(a._id))
  );
  const sharedModelId = selected.length === 1
    ? (selected[0].modelId?._id || selected[0].modelId || '')
    : '';
  const [modelId, setModelId] = useState(String(sharedModelId || ''));
  const [busy, setBusy] = useState(false);

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  const submit = async () => {
    if (!modelId) {
      toast.error('Select a model');
      return;
    }
    setBusy(true);
    try {
      await onConfirm(modelId);
      onClose();
    } catch (_) {
      // toast in caller
    } finally {
      setBusy(false);
    }
  };

  const title = selected.length === 1
    ? `Model · @${selected[0].username}`
    : `Model · ${selected.length} accounts`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`w-full max-w-md rounded-2xl shadow-xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
            <p className="text-xs text-gray-400 mt-0.5">Applies on next AI reply</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {selected.length > 1 && (
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {selected.slice(0, 6).map(a => `@${a.username}`).join(', ')}
              {selected.length > 6 ? ` +${selected.length - 6} more` : ''}
            </p>
          )}
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              AI Model
            </label>
            <select className={inputCls} value={modelId} onChange={e => setModelId(e.target.value)}>
              <option value="">Select model…</option>
              {models.map(m => (
                <option key={m._id} value={m._id}>{m.displayName || m.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} disabled={busy}
            className={`px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}>
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !modelId}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-50">
            {busy ? 'Saving…' : selected.length > 1 ? `Update ${selected.length}` : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const SnapChatbot = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Data state
  const [stats, setStats]               = useState(null);
  const [accounts, setAccounts]         = useState([]);
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents]             = useState([]);
  const [models, setModels]             = useState([]);

  // UI state
  const [activeTab, setActiveTab]       = useState('accounts');
  const [loading, setLoading]           = useState(true);
  const [convoLoading, setConvoLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [startAccount, setStartAccount] = useState(null); // account awaiting All/Per choice
  const [bulkStartIds, setBulkStartIds] = useState(null); // string[] for multi-start
  const [incomingAccount, setIncomingAccount] = useState(null);
  const [assignModelIds, setAssignModelIds] = useState(null); // string[] for model assign
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountUsers, setAccountUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userFilter, setUserFilter] = useState({ search: '', phase: '', needsReply: false, chattingOnly: false });

  // Conversation filters
  const [convoFilter, setConvoFilter]   = useState({ phase: '', needsReply: false, search: '', snapAccountId: '' });
  const [convoPage, setConvoPage]       = useState(1);
  const [convoTotal, setConvoTotal]     = useState(0);

  const LIMIT = 25;

  const cardCls = `rounded-xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const headingCls = `text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`;
  const subCls = `text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;

  // Fetch stats and accounts
  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, accRes, agentRes, modelRes] = await Promise.all([
        api.get('/snap/stats').catch(() => ({ data: { stats: null } })),
        api.get('/snap/accounts').catch(() => ({ data: { accounts: [] } })),
        isAdmin ? api.get('/admin/va/ai-agents').catch(() => ({ data: { agents: [] } })) : Promise.resolve({ data: { agents: [] } }),
        api.get('/models').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data?.stats);
      setAccounts(accRes.data?.accounts || []);
      const rawAgents = agentRes.data?.agents || agentRes.data || [];
      setAgents(Array.isArray(rawAgents) ? rawAgents : []);
      const rawModels = modelRes.data?.models || modelRes.data || [];
      setModels(Array.isArray(rawModels) ? rawModels : []);
    } catch (err) {
      console.error('Failed to load Snap overview:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  // Fetch conversations (server-side search + pagination)
  const loadConversations = useCallback(async () => {
    setConvoLoading(true);
    try {
      const params = new URLSearchParams({ page: String(convoPage), limit: String(LIMIT) });
      if (convoFilter.phase)        params.set('phase', convoFilter.phase);
      if (convoFilter.needsReply)   params.set('needsReply', 'true');
      if (convoFilter.chattingOnly) params.set('chattingEnabled', 'true');
      if (convoFilter.search.trim()) params.set('search', convoFilter.search.trim());
      if (convoFilter.snapAccountId) params.set('snapAccountId', convoFilter.snapAccountId);
      const res = await api.get(`/snap/conversations?${params}`);
      setConversations(res.data?.conversations || []);
      setConvoTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setConvoLoading(false);
    }
  }, [convoPage, convoFilter.phase, convoFilter.needsReply, convoFilter.chattingOnly, convoFilter.search, convoFilter.snapAccountId]);

  const loadAccountUsers = useCallback(async () => {
    if (!selectedAccountId) {
      setAccountUsers([]);
      setUsersTotal(0);
      return;
    }
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({ page: String(usersPage), limit: '50' });
      if (userFilter.phase) params.set('phase', userFilter.phase);
      if (userFilter.needsReply) params.set('needsReply', 'true');
      if (userFilter.chattingOnly) params.set('chattingEnabled', 'true');
      if (userFilter.search.trim()) params.set('search', userFilter.search.trim());
      const res = await api.get(`/snap/accounts/${selectedAccountId}/users?${params}`);
      setAccountUsers(res.data?.users || []);
      setUsersTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to load account users:', err);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [selectedAccountId, usersPage, userFilter.phase, userFilter.needsReply, userFilter.chattingOnly, userFilter.search]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    if (activeTab !== 'conversations') return;
    const t = setTimeout(() => loadConversations(), convoFilter.search ? 300 : 0);
    return () => clearTimeout(t);
  }, [activeTab, loadConversations, convoFilter.search, convoFilter.snapAccountId]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    const t = setTimeout(() => loadAccountUsers(), userFilter.search ? 300 : 0);
    return () => clearTimeout(t);
  }, [activeTab, loadAccountUsers, userFilter.search, selectedAccountId]);

  const openAccountUsers = (account) => {
    setSelectedAccountId(account._id);
    setUsersPage(1);
    setActiveTab('users');
  };

  const openUserConversation = (userRow) => {
    const conv = {
      _id: userRow.conversationId,
      snapTargetUsername: userRow.username,
      snapTargetDisplayName: userRow.displayName,
      snapConversationUuid: userRow.snapConversationUuid,
      conversationPhase: userRow.phase,
      chattingEnabled: userRow.chattingEnabled,
      needsReply: userRow.needsReply,
      userType: userRow.userType,
      userProfile: userRow.userProfile,
      snapAccountId: selectedAccountId
    };
    setSelectedConvo(conv);
  };

  const viewUserInConversationsTab = (userRow) => {
    setConvoFilter(f => ({
      ...f,
      search: userRow.username || '',
      snapAccountId: selectedAccountId
    }));
    setConvoPage(1);
    setActiveTab('conversations');
  };

  const handleAddAccount = async (form) => {
    try {
      const res = await api.post('/snap/accounts', form);
      const { gologinCreated, gologinFolderName } = res.data || {};
      if (gologinCreated) {
        toast.success(`Snapchat account added — GoLogin profile created in folder "${gologinFolderName}"`);
      } else {
        toast.success('Snapchat account added');
      }
      loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add account');
      throw err;
    }
  };

  const handlePauseAccount = async (account) => {
    // Indexing / waiting_login stick if the VPS agent dies mid-run — still pausable.
    const running = ['active', 'indexing', 'waiting_login'].includes(account.status);
    if (running) {
      try {
        await api.patch(`/snap/accounts/${account._id}`, { status: 'paused' });
        toast.success('Account paused');
        loadOverview();
      } catch (err) {
        toast.error('Failed to pause account');
      }
      return;
    }
    // Resume / start → choose All vs Per
    setStartAccount(account);
  };

  const handleConfirmStart = async (replyMode, { wipeConversations = false } = {}) => {
    if (bulkStartIds?.length) {
      try {
        const res = await api.post('/snap/accounts/bulk-start', {
          accountIds: bulkStartIds,
          replyMode,
          wipeConversations
        });
        toast.success(`Started ${res.data?.started ?? 0} account(s)`
          + (res.data?.failed ? `, ${res.data.failed} failed` : ''));
        setBulkStartIds(null);
        setSelectedIds(new Set());
        loadOverview();
        if (activeTab === 'conversations') loadConversations();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Bulk start failed');
        throw err;
      }
      return;
    }
    if (!startAccount) return;
    try {
      const res = await api.patch(`/snap/accounts/${startAccount._id}`, {
        status: 'active',
        replyMode,
        wipeConversations
      });
      const activated = res.data?.activated ?? 0;
      const queued = res.data?.queued ?? 0;
      const wiped = res.data?.wiped ?? 0;
      if (replyMode === 'all') {
        const wipeNote = wiped ? `, wiped ${wiped} old thread(s)` : '';
        toast.success(
          `All mode on — ${activated} thread(s) armed`
          + (queued ? `, ${queued} AI reply(ies) queued` : '')
          + wipeNote
        );
      } else {
        toast.success(
          wiped
            ? `Per mode — cleared ${wiped} thread(s), use Start on each chat`
            : 'Per-conversation mode — use Start on each thread'
        );
      }
      loadOverview();
      if (activeTab === 'conversations') loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start account');
      throw err;
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === accounts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(accounts.map(a => a._id)));
  };

  const handleAssignModel = async (modelId) => {
    if (!assignModelIds?.length) return;
    try {
      const res = await api.post('/snap/accounts/bulk-model', {
        accountIds: assignModelIds,
        modelId
      });
      const name = res.data?.model?.displayName || res.data?.model?.name || 'model';
      toast.success(`Assigned ${name} to ${res.data?.updated ?? 0} account(s)`
        + (res.data?.failed ? `, ${res.data.failed} failed` : ''));
      setAssignModelIds(null);
      setSelectedIds(new Set());
      loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update model');
      throw err;
    }
  };

  const handleWipeConversations = async (account) => {
    if (!window.confirm(
      `Delete all conversation history for @${account.username}? Messages in Mongo will be removed. The Snap account itself is kept.`
    )) return;
    try {
      const res = await api.post(`/snap/accounts/${account._id}/wipe-conversations`, { confirm: true });
      toast.success(`Cleared ${res.data?.deletedCount ?? 0} conversation(s) for @${account.username}`);
      loadOverview();
      if (activeTab === 'conversations') loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to wipe conversations');
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Delete Snapchat account @${account.username}? This cannot be undone.`)) return;
    try {
      await api.delete(`/snap/accounts/${account._id}`);
      toast.success('Account deleted');
      loadOverview();
    } catch (err) {
      toast.error('Failed to delete account');
    }
  };

  const handleToggleChatting = async (conv, enabled) => {
    try {
      await api.patch(`/snap/conversations/${conv._id}`, { chattingEnabled: enabled });
      toast.success(enabled ? `Chatting started for @${conv.snapTargetUsername || 'user'}` : `Chatting stopped`);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update conversation');
    }
  };

  // ─── Stats bar ──────────────────────────────────────────────────────────────
  const StatCard = ({ label, value, sub, color = 'blue' }) => {
    const colors = {
      blue:   'from-blue-500/10 to-blue-600/10 border-blue-200 dark:border-blue-800',
      green:  'from-green-500/10 to-green-600/10 border-green-200 dark:border-green-800',
      yellow: 'from-yellow-500/10 to-yellow-600/10 border-yellow-200 dark:border-yellow-800',
      red:    'from-red-500/10 to-red-600/10 border-red-200 dark:border-red-800',
    };
    return (
      <div className={`rounded-xl border bg-gradient-to-br ${colors[color]} p-4`}>
        <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
        <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value ?? '—'}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    );
  };

  const tabCls = (t) => `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
    activeTab === t
      ? isDarkMode
        ? 'bg-yellow-400/20 text-yellow-300 border border-yellow-500/30'
        : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
      : isDarkMode
        ? 'text-gray-400 hover:text-white hover:bg-gray-700'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
  }`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-yellow-400/20">
            <SnapchatIcon className="h-6 w-6 text-yellow-400" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Snapchat</h1>
            <p className={subCls}>Automated DM chatbot via VPS desktop agents</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadOverview}
            title="Refresh accounts & stats"
            className={`p-2 rounded-lg ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            <ArrowPathIcon className="h-4 w-4" />
          </button>
          {isAdmin && (
            <button onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-400 text-black text-sm font-medium hover:bg-yellow-300">
              <PlusIcon className="h-4 w-4" />
              Add Account
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Active Accounts" value={stats.accounts?.active} sub={`${stats.accounts?.total} total`} color="green" />
          <StatCard label="Conversations" value={stats.conversations?.total} sub={`${stats.conversations?.chatting ?? 0} chatting`} color="blue" />
          <StatCard label="Needs Reply" value={stats.conversations?.needsReply} color="yellow" />
          <StatCard label="Completed" value={stats.conversations?.completed} sub={`${stats.conversations?.ctaSent} CTA sent`} color="green" />
        </div>
      )}

      {/* Session expired warning */}
      {stats?.accounts?.expired > 0 && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${isDarkMode ? 'bg-orange-900/20 border-orange-700/50 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-700'}`}>
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <p className="text-sm font-medium">
            {stats.accounts.expired} account{stats.accounts.expired !== 1 ? 's have' : ' has'} an expired session — re-capture cookies and update via the account settings.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className={`flex items-center gap-2 p-1 rounded-xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <button className={tabCls('accounts')} onClick={() => setActiveTab('accounts')}>
          Accounts ({accounts.length})
        </button>
        <button className={tabCls('users')} onClick={() => setActiveTab('users')}>
          Users{selectedAccountId ? ` · @${accounts.find(a => String(a._id) === String(selectedAccountId))?.username || '?'}` : ''}
        </button>
        <button className={tabCls('conversations')} onClick={() => setActiveTab('conversations')}>
          Conversations
        </button>
        <button className={tabCls('setup')} onClick={() => setActiveTab('setup')}>
          Agent Setup
        </button>
      </div>

      {/* ── ACCOUNTS TAB ─────────────────────────────────────────────────────── */}
      {activeTab === 'accounts' && (
        <div className={cardCls}>
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <SnapchatIcon className="h-10 w-10 text-gray-300" />
              <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>No Snapchat accounts yet</p>
              <p className="text-sm text-gray-400">Add a GoLogin-managed Snap account to get started.</p>
              {isAdmin && (
                <button onClick={() => setShowAddModal(true)}
                  className="mt-2 flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-400 text-black text-sm font-medium hover:bg-yellow-300">
                  <PlusIcon className="h-4 w-4" /> Add Account
                </button>
              )}
            </div>
          ) : (
            <>
              {isAdmin && (
                <div className={`flex flex-wrap items-center gap-3 px-5 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === accounts.length && accounts.length > 0}
                      onChange={toggleSelectAll}
                    />
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Select all ({selectedIds.size})
                    </span>
                  </label>
                  <button
                    type="button"
                    disabled={!selectedIds.size}
                    onClick={() => {
                      setStartAccount(null);
                      setBulkStartIds([...selectedIds]);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-400 text-black hover:bg-yellow-300 disabled:opacity-40"
                  >
                    <PlayIcon className="h-4 w-4" />
                    Start selected
                  </button>
                  <button
                    type="button"
                    disabled={!selectedIds.size}
                    onClick={() => setAssignModelIds([...selectedIds])}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border disabled:opacity-40 ${
                      isDarkMode
                        ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <PencilIcon className="h-4 w-4" />
                    Set model
                  </button>
                </div>
              )}
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {accounts.map(account => (
                <div key={account._id} className="flex items-center justify-between px-5 py-4 gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    {isAdmin && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(account._id)}
                        onChange={() => toggleSelect(account._id)}
                      />
                    )}
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                      isDarkMode ? 'bg-yellow-400/20 text-yellow-300' : 'bg-yellow-50 text-yellow-600'
                    }`}>
                      {account.displayName?.[0]?.toUpperCase() || 'S'}
                    </div>
                    <div className="min-w-0">
                      <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{account.displayName}</p>
                      <p className="text-xs text-gray-400">@{account.username}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400">
                      <SignalIcon className="h-3.5 w-3.5" />
                      {account.vaAgentId?.name || 'No agent'}
                    </div>
                    {(account.modelId?.displayName || account.modelId?.name) && (
                      <div className="hidden md:flex items-center gap-1 text-xs text-gray-400">
                        <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                        {account.modelId.displayName || account.modelId.name}
                      </div>
                    )}
                    <StatusBadge status={account.status} />
                    {account.config?.incomingAdds?.enabled && (
                      <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                        title="Incoming friend-add automation">
                        Adds
                      </span>
                    )}
                    {account.config?.autoReplyAll && account.status === 'active' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-yellow-400/20 text-yellow-700 dark:text-yellow-300">
                        All replies
                      </span>
                    )}
                    {account.status === 'active' && (
                      <span
                        className="hidden xl:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-300"
                        title="Memory inbox + deep scan unread only"
                      >
                        CA scan · {account.config?.maxConcurrentDeepScans ?? 3}×
                      </span>
                    )}
                    {account.lastSeenAt && (
                      <span className="hidden lg:block text-xs text-gray-400" title={formatAmsterdamDateTime(account.lastSeenAt)}>
                        {formatAmsterdamTs(account.lastSeenAt)}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => openAccountUsers(account)}
                      className={`hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${
                        isDarkMode
                          ? 'text-yellow-300 hover:bg-gray-700 border border-gray-600'
                          : 'text-yellow-800 hover:bg-yellow-50 border border-yellow-200'
                      }`}
                      title="View users and lead profiles">
                      <UserIcon className="h-3.5 w-3.5" />
                      Users
                    </button>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => setAssignModelIds([account._id])}
                          className={`p-1.5 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-yellow-300 hover:bg-gray-700' : 'text-gray-500 hover:text-yellow-600 hover:bg-gray-100'}`}
                          title="Change AI model">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setIncomingAccount(account)}
                          className={`p-1.5 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-emerald-300 hover:bg-gray-700' : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-100'}`}
                          title="Incoming adds filter & log">
                          <UserIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleWipeConversations(account)}
                          className={`p-1.5 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-orange-300 hover:bg-gray-700' : 'text-gray-500 hover:text-orange-600 hover:bg-gray-100'}`}
                          title="Clear all conversation history (Mongo)">
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => handlePauseAccount(account)}
                          className={`p-1.5 rounded-lg text-xs ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                          title={['active', 'indexing', 'waiting_login'].includes(account.status) ? 'Pause' : 'Resume'}>
                          {['active', 'indexing', 'waiting_login'].includes(account.status)
                            ? <StopIcon className="h-4 w-4" />
                            : <PlayIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={() => handleDeleteAccount(account)}
                          className={`p-1.5 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-500 hover:text-red-500 hover:bg-gray-100'}`}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ── USERS TAB (per account lead overview) ───────────────────────────── */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className={`flex flex-wrap items-center gap-3 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Account</label>
            <select
              value={selectedAccountId}
              onChange={e => { setSelectedAccountId(e.target.value); setUsersPage(1); }}
              className={`px-3 py-1.5 rounded-lg border text-sm min-w-[200px] ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
              <option value="">Select account…</option>
              {accounts.map(a => (
                <option key={a._id} value={a._id}>@{a.username}{a.displayName ? ` (${a.displayName})` : ''}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 flex-1 min-w-48">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              <input
                className={`flex-1 bg-transparent text-sm outline-none ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                placeholder="Search user…"
                value={userFilter.search}
                onChange={e => { setUserFilter(f => ({ ...f, search: e.target.value })); setUsersPage(1); }}
                disabled={!selectedAccountId}
              />
            </div>
            <select
              value={userFilter.phase}
              onChange={e => { setUserFilter(f => ({ ...f, phase: e.target.value })); setUsersPage(1); }}
              disabled={!selectedAccountId}
              className={`px-3 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
              <option value="">All phases</option>
              <option value="initial">Initial</option>
              <option value="phase_1">Phase 1</option>
              <option value="phase_2">Phase 2</option>
              <option value="phase_3">Phase 3</option>
              <option value="cta">CTA</option>
              <option value="completed">Completed</option>
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={userFilter.needsReply}
                onChange={e => { setUserFilter(f => ({ ...f, needsReply: e.target.checked })); setUsersPage(1); }}
                disabled={!selectedAccountId} className="rounded" />
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Needs reply</span>
            </label>
            <button type="button" onClick={loadAccountUsers} disabled={!selectedAccountId || usersLoading}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm disabled:opacity-40 ${
                isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}>
              <ArrowPathIcon className={`h-4 w-4 ${usersLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className={cardCls}>
            {!selectedAccountId ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <UserIcon className="h-10 w-10 text-gray-300" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Select an account to view users and lead profiles</p>
              </div>
            ) : usersLoading ? (
              <div className="flex justify-center py-12">
                <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" />
              </div>
            ) : accountUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No users found for this account</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {accountUsers.map(u => (
                  <div key={u.conversationId} className={`p-4 ${isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {u.displayName || u.username || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {u.username ? `@${u.username}` : u.snapConversationUuid?.slice(0, 8)}
                          {' · '}{u.exchangeCount || 0} exchanges
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <PhaseBadge phase={u.phase} />
                        {u.needsReply && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-600 dark:text-yellow-300">needs reply</span>
                        )}
                        {u.chattingEnabled && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-300">chatting</span>
                        )}
                      </div>
                    </div>
                    <UserProfilePanel profile={u.userProfile} userType={u.userType} isDarkMode={isDarkMode} compact />
                    <div className="flex items-center gap-2 mt-3">
                      <button type="button" onClick={() => openUserConversation(u)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-yellow-400 text-black hover:bg-yellow-300">
                        <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                        Open chat
                      </button>
                      <button type="button" onClick={() => viewUserInConversationsTab(u)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                          isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}>
                        All threads
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedAccountId && usersTotal > 50 && (
              <div className={`flex items-center justify-between px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className="text-xs text-gray-400">{usersTotal} users total</p>
                <div className="flex gap-2">
                  <button disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)}
                    className="px-2 py-1 text-xs rounded border disabled:opacity-40">Prev</button>
                  <button disabled={usersPage * 50 >= usersTotal} onClick={() => setUsersPage(p => p + 1)}
                    className="px-2 py-1 text-xs rounded border disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CONVERSATIONS TAB ─────────────────────────────────────────────────── */}
      {activeTab === 'conversations' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className={`flex flex-wrap items-center gap-3 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <select
              value={convoFilter.snapAccountId || ''}
              onChange={e => { setConvoFilter(f => ({ ...f, snapAccountId: e.target.value })); setConvoPage(1); }}
              className={`px-3 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
              <option value="">All accounts</option>
              {accounts.map(a => (
                <option key={a._id} value={a._id}>@{a.username}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 flex-1 min-w-48">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              <input
                className={`flex-1 bg-transparent text-sm outline-none ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                placeholder="Search username or display name…"
                value={convoFilter.search}
                onChange={e => {
                  setConvoFilter(f => ({ ...f, search: e.target.value }));
                  setConvoPage(1);
                }}
              />
            </div>
            <select
              value={convoFilter.phase}
              onChange={e => { setConvoFilter(f => ({ ...f, phase: e.target.value })); setConvoPage(1); }}
              className={`px-3 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
              <option value="">All phases</option>
              <option value="initial">Initial</option>
              <option value="phase_1">Phase 1</option>
              <option value="phase_2">Phase 2</option>
              <option value="phase_3">Phase 3</option>
              <option value="cta">CTA</option>
              <option value="completed">Completed</option>
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={convoFilter.needsReply}
                onChange={e => { setConvoFilter(f => ({ ...f, needsReply: e.target.checked })); setConvoPage(1); }}
                className="rounded" />
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Needs reply</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={convoFilter.chattingOnly || false}
                onChange={e => { setConvoFilter(f => ({ ...f, chattingOnly: e.target.checked })); setConvoPage(1); }}
                className="rounded" />
              <span className={isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}>Chatting only</span>
            </label>
            <button
              type="button"
              onClick={loadConversations}
              disabled={convoLoading}
              title="Refresh conversations"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm ${
                isDarkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50'
              }`}>
              <ArrowPathIcon className={`h-4 w-4 ${convoLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Conversation list */}
          <div className={cardCls}>
            {convoLoading ? (
              <div className="flex justify-center py-12">
                <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <ChatBubbleLeftRightIcon className="h-10 w-10 text-gray-300" />
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No conversations found
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {conversations.map(conv => {
                  const account = accounts.find(a =>
                    String(a._id) === String(conv.snapAccountId?._id || conv.snapAccountId)
                  );
                  const fromUser = conv.snapAccountId?.username || account?.username || '—';
                  const fromLabel = conv.snapAccountId?.displayName || account?.displayName || fromUser;
                  const toUser = conv.snapTargetUsername || null;
                  const toName = conv.snapTargetDisplayName || null;
                  const toLabel = toName || snapPeerLabel(conv);
                  const msgCount = conv.messageCount ?? conv.messages?.length ?? 0;
                  const startedTs = conv.startedAt || conv.createdAt || null;
                  const lastActiveTs = conv.lastMessageAt || conv.lastActiveAt || conv.lastInboundAt || conv.updatedAt;

                  return (
                  <div
                    key={conv._id}
                    className={`px-5 py-3.5 transition-colors ${
                      conv.chattingEnabled
                        ? isDarkMode ? 'bg-yellow-400/5 border-l-2 border-yellow-400' : 'bg-yellow-50 border-l-2 border-yellow-400'
                        : isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                    }`}>
                    <div className="flex items-center justify-between gap-3">
                      {/* Left: avatar + From → To */}
                      <button
                        className="flex items-center gap-3 text-left flex-1 min-w-0"
                        onClick={() => setSelectedConvo(conv)}>
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          conv.chattingEnabled
                            ? 'bg-yellow-400 text-black'
                            : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {(toName || toUser || conv.snapConversationUuid || '?')[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {toLabel}
                              {toName && toUser ? (
                                <span className="ml-1.5 font-normal text-gray-400">@{toUser}</span>
                              ) : null}
                            </p>
                            {conv.needsReply && conv.chattingEnabled && (
                              <span className="h-2 w-2 rounded-full bg-yellow-400 shrink-0" title="Needs reply" />
                            )}
                          </div>
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                              {fromLabel}
                            </span>
                            <span className="mx-1.5 text-gray-500">→</span>
                            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                              {toLabel}
                            </span>
                            {msgCount > 0 && (
                              <span className="ml-2">{msgCount} msg{msgCount !== 1 ? 's' : ''}</span>
                            )}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5 tabular-nums">
                            <span title={startedTs ? formatAmsterdamDateTime(startedTs) : ''}>
                              Started {formatAmsterdamTs(startedTs)}
                            </span>
                            <span className="mx-1.5 text-gray-500">·</span>
                            <span title={lastActiveTs ? formatAmsterdamDateTime(lastActiveTs) : ''}>
                              Last Active {formatAmsterdamTs(lastActiveTs)}
                            </span>
                          </p>
                          {conv.lastMessagePreview && (
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">
                              {conv.lastMessagePreview}
                            </p>
                          )}
                          {conv.followUpEta && conv.chattingEnabled && conv.conversationPhase !== 'completed' && (
                            <p className="text-[11px] text-amber-500/90 mt-0.5 flex items-center gap-1">
                              <ClockIcon className="h-3 w-3 shrink-0" />
                              <span>
                                {conv.followUpTypeLabel || 'Follow-up'} {conv.followUpEta}
                              </span>
                            </p>
                          )}
                        </div>
                      </button>

                      {/* Right: badges + actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        <PhaseBadge phase={conv.conversationPhase} />
                        <span
                          className="hidden sm:block text-xs text-gray-400 tabular-nums"
                          title={lastActiveTs ? formatAmsterdamDateTime(lastActiveTs) : ''}
                        >
                          {formatAmsterdamTs(lastActiveTs)}
                        </span>
                        {isAdmin && !(
                          account?.config?.autoReplyAll
                        ) && (
                          conv.chattingEnabled ? (
                            <button
                              onClick={() => handleToggleChatting(conv, false)}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-yellow-400 text-black hover:bg-yellow-300 transition-colors"
                              title="Stop chatting for this conversation">
                              <StopIcon className="h-3 w-3" />
                              Stop
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleChatting(conv, true)}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                isDarkMode
                                  ? 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-400/10'
                                  : 'border-yellow-400 text-yellow-700 hover:bg-yellow-50'
                              }`}
                              title="Start AI chatting for this conversation">
                              <PlayIcon className="h-3 w-3" />
                              Start
                            </button>
                          )
                        )}
                        {isAdmin && account?.config?.autoReplyAll && (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-yellow-600 dark:text-yellow-400 px-1.5">
                            Auto
                          </span>
                        )}
                        <button
                          onClick={() => setSelectedConvo(conv)}
                          className={`p-1 rounded ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-300 hover:text-gray-500'}`}>
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2.5 pl-12">
                      <SnapPhaseProgress conversation={conv} />
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {convoTotal > LIMIT && (
              <div className={`flex items-center justify-between px-5 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <p className="text-xs text-gray-400">{convoTotal} total</p>
                <div className="flex gap-2">
                  <button disabled={convoPage <= 1} onClick={() => setConvoPage(p => p - 1)}
                    className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-200 disabled:opacity-40' : 'bg-gray-100 text-gray-700 disabled:opacity-40'}`}>
                    Prev
                  </button>
                  <span className="text-xs text-gray-400 self-center">Page {convoPage}</span>
                  <button disabled={convoPage * LIMIT >= convoTotal} onClick={() => setConvoPage(p => p + 1)}
                    className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-200 disabled:opacity-40' : 'bg-gray-100 text-gray-700 disabled:opacity-40'}`}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SETUP TAB ─────────────────────────────────────────────────────────── */}
      {activeTab === 'setup' && (
        <SnapAgentSetupTab agents={agents} isDarkMode={isDarkMode} onRefresh={loadOverview} />
      )}

      {/* Modals */}
      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddAccount}
          agents={agents}
          models={models}
        />
      )}
      {startAccount && (
        <StartReplyModeModal
          account={startAccount}
          isDarkMode={isDarkMode}
          onClose={() => setStartAccount(null)}
          onConfirm={handleConfirmStart}
        />
      )}
      {bulkStartIds?.length > 0 && (
        <StartReplyModeModal
          account={{ username: `${bulkStartIds.length} accounts` }}
          isDarkMode={isDarkMode}
          onClose={() => setBulkStartIds(null)}
          onConfirm={handleConfirmStart}
        />
      )}
      {incomingAccount && (
        <IncomingAddsModal
          account={incomingAccount}
          isDarkMode={isDarkMode}
          onClose={() => setIncomingAccount(null)}
          onSaved={(updated) => {
            if (updated) {
              setAccounts(prev => prev.map(a =>
                String(a._id) === String(updated._id) ? { ...a, ...updated } : a
              ));
            } else {
              loadOverview();
            }
          }}
        />
      )}
      {assignModelIds?.length > 0 && (
        <AssignModelModal
          accountIds={assignModelIds}
          accounts={accounts}
          models={models}
          isDarkMode={isDarkMode}
          onClose={() => setAssignModelIds(null)}
          onConfirm={handleAssignModel}
        />
      )}
      {selectedConvo && (
        <MessageDrawer conversation={selectedConvo} onClose={() => setSelectedConvo(null)} />
      )}
    </div>
  );
};

export default SnapChatbot;
